import { withSentryAssertion } from '@/helpers/withSentryAssertion'

import {
  array,
  bool,
  CheckerReturnType,
  nullable,
  number,
  object,
  optional,
  string,
  union,
  voidable,
  dict,
  tuple,
  or,
  literal,
} from '@recoiljs/refine'

export const Booleanish = or(
  union(literal('True'), literal('true'), literal('false'), literal('False')),
  bool(),
)

export const SAPIProductOptionValue = object({
  option_display_name: string(),
  option_id: number(),
  id: number(),
  label: string(),
})

export const SAPIProductVariant = object({
  images: array(object({
    url_thumbnail: string(),
    url_square: string(),
    url_standard: string(),
    url_zoom: string(),
  })),
  calculated_price: number(),
  option_values: array(SAPIProductOptionValue),
  // upc: string(),
  colorChips: array(string()),
  sale_price: number(),
  inventory_level: number(),
  price: number(),
  product_id: number(),
  lead_time: optional(number()),
  name: string(),
  purchasing_disabled: bool(),
  id: number(),
  sku: string(),
})

export const SAPIProductOption = object({
  product_id: number(),
  option_values: array(SAPIProductOptionValue),
  name: string(),
  id: number(),
  display_name: string(),
  type: string(),
  sort_order: number(),
  // config: array(unknown),
})

export const SAPIProductWarrantyVariantImage = object({
  url: string(),
  // sort_order: number(),
  // hash: string(),
  // filename: string(),
})

export type SAPIProductWarrantyVariantImageType =
  CheckerReturnType<typeof SAPIProductWarrantyVariantImage>

export const SAPIProductWarrantyVariant = object({
  application_type: optional(string()),
  inventoryType: optional(number()),
  inventoryLevel: optional(number()),
  baseDateForDeliveryCalculation: optional(nullable(string())),
  materials: optional(string()),
  arm_height: optional(string()),
  art_quality: optional(string()),
  artist_commission: optional(string()),
  artist_id: optional(string()),
  artist: optional(string()),
  assembly_required: optional(bool()),
  authors: optional(string()),
  backplate_dimensions: optional(string()),
  base_dimensions: optional(string()),
  bed_clearance_height: optional(string()),
  bed_footboard_dims: optional(string()),
  bed_size: optional(string()),
  bedding_size: optional(string()),
  bedrails_dims: optional(string()),
  blend_material_percentage: optional(string()),
  book_type: optional(string()),
  bulb_included: optional(string()),
  bulb_type: optional(string()),
  bulb_watts: optional(string()),
  burn_time: optional(string()),
  canopy_dimensions: optional(string()),
  capacity: optional(string()),
  care: optional(string()),
  case_pk_qty: optional(number()),
  chain_length: optional(string()),
  clearance_height: optional(string()),
  closure_type: optional(string()),
  color_family: optional(string()),
  comfort_level: optional(string()),
  condition: optional(string()),
  construction: optional(string()),
  contemporary_original: optional(string()),
  contract_grade: optional(union(string(), bool())),
  cord_length: optional(string()),
  damage: optional(string()),
  details: optional(string()),
  dimensions_uom: optional(string()),
  dimensions: optional(string()),
  distance_between_arms: optional(string()),
  era: optional(string()),
  estimated_max_delivery_days: optional(union(string(), number())),
  estimated_min_delivery_days: optional(union(string(), number())),
  evergreen:  optional(union(string(), number())),
  exclusive:  optional(union(string(), number())),
  fill: optional(string()),
  fixture_frame_dims: optional(string()),
  floral_type: optional(string()),
  frame_color: optional(string()),
  frame_details: optional(string()),
  frame_materials: optional(string()),
  framed: optional(bool()),
  fulfillment_method: optional(string()),
  full_cost: optional(number()),
  gtin: optional(nullable(number())),
  handmade_machinemade: optional(string()),
  hardware_color: optional(string()),
  hardware_material: optional(string()),
  hardwired: optional(union(string(), bool())),
  hidden_zipper: optional(union(string(), bool())),
  images: optional(array(SAPIProductWarrantyVariantImage)),
  imprint: optional(string()),
  includes: optional(string()),
  ingredients: optional(string()),
  is_entryway: optional(bool()),
  is_free_shipping: optional(bool()),
  is_returnable: optional(bool()),
  is_vintage: optional(bool()),
  is_white_glove: optional(bool()),
  isbn: optional(string()),
  // lead_time: optional(number()),
  leg_height: optional(string()),
  legacy_variant_id: optional(number()),
  made_in: optional(string()),
  made_to_order: optional(string()),
  map_price: optional(nullable(number())),
  mat_board_color: optional(string()),
  mat_board_material: optional(string()),
  mat_board_options: optional(string()),
  mat_board_width: optional(string()),
  material_family: optional(string()),
  medium: optional(string()),
  number_of_boxes: optional(union(number(), string())),
  number_of_rolls: optional(string()),
  outdoor: optional(Booleanish),
  package_height: optional(string()),
  package_length: optional(string()),
  package_weight: optional(string()),
  package_width: optional(string()),
  packaging_type: optional(string()),
  page_count: optional(string()),
  pattern_direction: optional(string()),
  pattern: optional(string()),
  performance_fabric: optional(Booleanish),
  period: optional(string()),
  pile_height: optional(string()),
  pillowcase_size: optional(string()),
  please_note: optional(string()),
  primary_color_hex: optional(string()),
  primary_color: optional(string()),
  primary_finish: optional(string()),
  primary_letter: optional(string()),
  primary_material: optional(string()),
  primary_size: optional(string()),
  prop_65_compliant: optional(string()),
  quantity_on_hand: optional(nullable(number())),
  removable_cushions: optional(Booleanish),
  removable_legs: optional(Booleanish),
  reversible_cushions: optional(Booleanish),
  rod_length: optional(string()),
  rug_pad_recommended: optional(string()),
  rug_size: optional(string()),
  scent_life: optional(string()),
  scent: optional(string()),
  seat_depth: optional(string()),
  seat_height: optional(string()),
  secondary_color_hex: optional(string()),
  secondary_color: optional(string()),
  shade_dims: optional(string()),
  shade_finish: optional(string()),
  shade_material: optional(string()),
  size_family: optional(string()),
  sloped_ceiling: optional(union(string(), bool())),
  special_care: optional(string()),
  square_footage_per_roll: optional(string()),
  style: optional(string()),
  subject_matter: optional(string()),
  substrate_type: optional(string()),
  switch_type: optional(string()),
  thread_count: optional(string()),
  // upc: optional(nullable(number())),
  upholstery_color: optional(string()),
  upholstery_construction: optional(string()),
  upholstery_material: optional(string()),
  variant_type: optional(string()),
  vendor_quantity: optional(nullable(number())),
  vendor_sku: optional(string()),
  wallpaper_application: optional(string()),
  wallpaper_pattern_scale: optional(string()),
  wallpaper_removal: optional(union(string(), bool())),
  wallpaper_repeat: optional(string()),
  weight_uom: optional(string()),
  weight: optional(string()),
  wet_rated: optional(union(string(), bool())),
  wood_type: optional(string()),
})

export type WarrantyVariantType = CheckerReturnType<typeof SAPIProductWarrantyVariant>

export const SAPIProductWarrantyVariants = dict(SAPIProductWarrantyVariant)

export type SAPIProductWarrantyVariantsType = CheckerReturnType<typeof SAPIProductWarrantyVariants>

export const SAPIProductWarranty = object({
  variants: SAPIProductWarrantyVariants,
  attributes: optional(voidable(array(object({
    id: number(),
    name: string(),
    value: array(string()),
  })))),
  additional: object({
    brand_code: optional(string()),
    colors: optional(string()),
    created_date: optional(string()),
    finishes: optional(string()),
    ingestion_id: optional(number()),
    is_digital: optional(bool()),
    legacy_product_id: optional(string()),
    // listrak_master_sku: string(),
    // master_vendor_id: optional(string()),
    materials: optional(string()),
    modified_date: optional(string()),
    planning_hierarchy: optional(array(string())),
    primary_category_breadcrumb: optional(string()),
    primary_variant_sku: optional(union(number(), string())),
    sizes: optional(string()),
    vendor_code: optional(union(number(), string())),
    vendor_name: optional(string()),
  }),
})

export type SAPIProductWarrantyType = CheckerReturnType<typeof SAPIProductWarranty>

export const SAPIProductAdditionalInfo = object({
  artist_name: optional(string()),
  is_artisanal: optional(Booleanish),
  'Facet Color Family': optional(tuple(string())),
  'Made of': optional(tuple(string())),
  'Frame details': optional(tuple(string())),
  Dimensions: optional(tuple(string())),
  Finish: optional(tuple(string())),
  is_vmf: optional(tuple(string())),
  uom: optional(tuple(string())),
  'Product Code': optional(tuple(string())),
  'Master Vendor Id': optional(tuple(string())),
  free_shipping: optional(tuple(string())),
  price_range: optional(tuple(string())),
  sale: optional(tuple(string())),

  created_date: optional(string()),
  modified_date: optional(string()),
  ingestion_id: optional(number()),
  // listrak_master_sku: optional(string()),
  legacy_product_id: optional(string()),
  brand_code: optional(string()),
  // master_vendor_id: optional(string()),
  primary_category_breadcrumb: optional(string()),
  colors: optional(string()),
  materials: optional(string()),
  finishes: optional(string()),
  sizes: optional(string()),

  is_digital: optional(bool()),
  vendor_code: optional(or(string(), number())),
  'Brand Code': optional(string()),
  number_of_boxes: optional(string()),
  dimensions_uom: optional(string()),
  estimated_max_delivery_days: optional(string()),
  is_entryway: optional(Booleanish),
  // lead_time: optional(string()),
  exclusive: optional(string()),
  is_free_shipping: optional(Booleanish),
  'Primary Category Breadcrumb': optional(string()),
  is_white_glove: optional(Booleanish),
  made_to_order: optional(string()),
  is_vintage: optional(Booleanish),
  'Vendor Code': optional(string()),
  primary_variant_sku: optional(number()),
  estimated_min_delivery_days: optional(string()),
  care: optional(string()),
  style: optional(string()),
  performance_fabric: optional(Booleanish),
  period: optional(string()),
})

export type SAPIProductAdditionalInfoType = CheckerReturnType<typeof SAPIProductAdditionalInfo>

export const SAPIProductBreadCrumb = object({
  path: string(),
  parent_id: number(),
  name: string(),
  id: number(),
  url: string(),
})

export type SAPIProductBreadCrumbType = CheckerReturnType<typeof SAPIProductBreadCrumb>

export const SAPIProductPrice = object({
  calculated_price: number(),
  price: number(),
  sale_price: number(),
  variants: array(object({
    sku: string(),
    calculated_price: number(),
    price: number(),
    id: number(),
    sale_price: number(),
  })),
})

export type SAPIProductPriceType = CheckerReturnType<typeof SAPIProductPrice>

export const SAPIProduct = object({
  calculated_price: number(),
  // gift_wrapping_options_list: array(unknow),
  page_title: string(),
  is_condition_shown: bool(),
  variants: array(SAPIProductVariant),
  type: string(),
  map_price: number(),
  retail_price: number(),
  layout_file: string(),
  price: number(),
  inventory_warning_level: number(),
  options: array(object({
    product_id: number(),
    option_values: array(object({
      id: number(),
      label: string(),
      value_data: nullable(or(string(), number())),
      is_default: bool(),
      sort_order: number(),
    })),
    name: string(),
    id: number(),
    display_name: string(),
    type: string(),
    sort_order: number(),
  })),
  warranty: SAPIProductWarranty,
  is_free_shipping: bool(),
  id: number(),
  total_sold: number(),
  sku: string(),
  brand: nullable(string()),
  height: number(),
  custom_url: object({
    is_customized: bool(),
    url: string(),
  }),
  open_graph_use_meta_description: bool(),
  weight: number(),
  // upc: string(),
  mpn: string(),
  open_graph_type: string(),
  open_graph_use_image: bool(),
  brand_id: number(),
  meta_description: string(),
  condition: string(),
  inventory_level: number(),
  // additional_info: SAPIProductAdditionalInfo,
  name: string(),
  open_graph_description: string(),
  open_graph_use_product_name: bool(),
  reviews_rating_sum: number(),
  breadcrumbs: array(SAPIProductBreadCrumb),
  reviews_count: number(),
  brand_code: optional(string()),
  bestSellingScore: number(),
  inventory_tracking: string(),
  // preorder_release_date: nullable(bool()),
  description: string(),
  bin_picking_number: string(),
  colorChips: array(object({
    sku: string(),
    colors: array(string()),
  })),
  availability: string(),
  search_keywords: string(),
  meta_keywords: array(string()),
  related_products: array(number()),
  is_price_hidden: bool(),
  order_quantity_minimum: number(),
  availability_description: string(),
  fixed_cost_shipping_price: number(),
  categories: array(number()),
  base_variant_id: nullable(number()),
  prices: SAPIProductPrice,
  sort_order: number(),
  cost_price: number(),
  option_set_display: string(),
  gtin: string(),
  order_quantity_maximum: number(),
  category_info: array(object({
    path: string(),
    parent_id: number(),
    name: string(),
    id: number(),
    url: string(),
  })),
  is_visible: bool(),
  is_preorder_only: bool(),
  date_created: string(),
  preorder_message: string(),
  // master_vendor_id: string(),
  tax_class_id: number(),
  open_graph_title: string(),
  isNew: bool(),
  sale_price: number(),
  product_tax_code: string(),
  depth: number(),
  max_price: number(),
  date_modified: string(),
  min_price: number(),
  is_on_sale: bool(),
  gift_wrapping_options_type: string(),
  width: number(),
  readyToShip: bool(),
  price_hidden_label: string(),
  is_featured: bool(),
  view_count: number(),
  reviewRating: optional(number()),
  reviewCount: optional(number()),
})

export const SAPIProducts = array(SAPIProduct)

export type SAPIProductType = CheckerReturnType<typeof SAPIProduct>
export const assertSAPIProduct = withSentryAssertion([SAPIProduct, 'Search API Product'])

export type SAPIProductsType = CheckerReturnType<typeof SAPIProducts>

export const assertSAPIProducts = withSentryAssertion([SAPIProducts, 'Search API Products Array'])
